import { Link } from "react-router-dom";
import { COMPANY } from "@/lib/brands";
import Logo from "@/components/site/Logo";
import { Reveal, RevealWords } from "@/components/site/Reveal";
import useSEO from "@/lib/useSEO";

const FACTS = [
  { label: "Nature of Business", value: "Exporter, Manufacturer & Retail" },
  { label: "Team", value: "11 – 25 People" },
  { label: "Established", value: COMPANY.est },
  { label: "GST Number", value: COMPANY.gst },
  { label: "IndiaMART", value: "Trust Seal Verified" },
  { label: "IEC Code", value: "Registered Exporter" },
];

export default function About() {
  useSEO({
    title: "About RSA Overseas Private Limited — Est. 2018 · Kamshet, Pune",
    description:
      "RSA Overseas Pvt. Ltd. is a Pune-based importer of seeds & hay and manufacturer of premium animal feeds. Trusted by 5,000+ clients across India and the world. GST 27AAJCR1114N1Z8.",
    path: "/about",
  });
  return (
    <div data-testid="about-page" className="bg-parchment text-aubergine">
      {/* HERO */}
      <section className="relative bg-aubergine text-parchment overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-90"
          style={{
            backgroundImage:
              "radial-gradient(1000px 500px at 20% 30%, rgba(225,29,96,0.24), transparent 60%), radial-gradient(800px 400px at 90% 90%, rgba(147,51,234,0.18), transparent 60%)",
          }}
        />
        <div aria-hidden className="absolute inset-0 grain-overlay" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-24 md:pb-32">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4 mb-10">
              <span className="h-px w-8 bg-magenta/60" />
              About RSA Overseas
            </div>
          </Reveal>
          <h1
            data-testid="about-heading"
            className="font-display font-light text-[52px] sm:text-7xl md:text-8xl lg:text-[128px] tracking-[-0.045em] leading-[0.9] text-parchment text-balance max-w-6xl"
          >
            <RevealWords text="Importers" delay={0.1} />
            <br />
            <RevealWords text="of" delay={0.25} />{" "}
            <span className="italic">
              <RevealWords text="Seeds & Hay." delay={0.35} />
            </span>
            <br />
            <RevealWords text="Manufacturers" delay={0.55} />{" "}
            <RevealWords text="of" delay={0.7} />
            <br />
            <span className="italic text-magenta">
              <RevealWords text="Animal Feeds." delay={0.8} />
            </span>
          </h1>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="bg-parchment">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-x-16 gap-y-14">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                  Who we are
                </div>
                <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl mt-8 tracking-[-0.035em] leading-[0.94] text-aubergine text-balance">
                  A quiet Pune house of{" "}
                  <span className="italic text-magenta">formulators, farmers</span>{" "}
                  and exporters.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <figure className="mt-14 relative">
                  <div
                    className="relative aspect-[3/4] overflow-hidden"
                    style={{
                      borderTopLeftRadius: "50% 32%",
                      borderTopRightRadius: "50% 32%",
                    }}
                  >
                    <img
                      src="https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                      alt="Hay bales in warehouse"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <figcaption className="mt-4 text-xs italic font-display text-ink/60">
                    Our Kamshet warehouse — where every bale is inspected by hand.
                  </figcaption>
                </figure>
              </Reveal>
            </div>

            <div className="lg:col-span-6 lg:col-start-7 lg:pt-16">
              <div className="space-y-6 text-lg text-ink/75 leading-[1.65] max-w-xl">
                <Reveal>
                  <p className="font-display italic text-2xl md:text-3xl text-aubergine leading-snug">
                    Established in {COMPANY.est}, RSA Overseas Private Limited
                    is a Pune-based house that imports premium Seeds and Hay,
                    and manufactures the Animal Feeds those raw materials
                    become.
                  </p>
                </Reveal>
                <Reveal delay={0.15}>
                  <p>
                    We import <strong>Sunflower Seeds</strong>,{" "}
                    <strong>Raw Oats</strong> and four hay varieties —{" "}
                    <strong>Timothy</strong>, <strong>Alfalfa</strong>,{" "}
                    <strong>Meadow</strong> and <strong>Orchard</strong> —
                    from certified growers across the globe, then manufacture
                    complete feeds under our four in-house brands.
                  </p>
                </Reveal>
                <Reveal delay={0.25}>
                  <p>
                    Our team formulates these commodities into complete
                    nutritional lines under <strong>EquiOne</strong> (equine),{" "}
                    <strong>Hopsy</strong> (small pets),{" "}
                    <strong>AviPure</strong> (avian) and{" "}
                    <strong>NutriMax</strong> (human pantry) — combining
                    agronomy, veterinary nutrition and a deep respect for the
                    animals and people we serve.
                  </p>
                </Reveal>
              </div>

              <Reveal delay={0.3}>
                <ul className="mt-12 divide-y divide-aubergine/15 border-y border-aubergine/15">
                  {[
                    "Constant training & seminars for our workforce",
                    "Stringent vendor consideration & verification",
                    "Updated processing methods for nutritious fodder",
                    "Decades of cumulative industry experience",
                  ].map((p, i) => (
                    <li
                      key={p}
                      className="flex items-baseline gap-6 py-5 text-aubergine"
                    >
                      <span className="font-display italic font-light text-2xl text-magenta w-10">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-medium">{p}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* FACTS */}
      <section className="bg-aubergine text-parchment relative overflow-hidden grain-overlay">
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-y-8 items-end mb-16">
              <div className="md:col-span-8">
                <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta/90 flex items-center gap-4">
                  <span className="h-px w-8 bg-magenta/60" />
                  The fine print
                </div>
                <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl mt-8 tracking-[-0.035em] leading-[0.94] text-parchment">
                  The house{" "}
                  <span className="italic">at a glance.</span>
                </h2>
              </div>
              <div className="md:col-span-4 md:pl-6 flex md:justify-end">
                <div className="h-24 w-24 md:h-32 md:w-32 rounded-full overflow-hidden ring-1 ring-parchment/20 bg-parchment">
                  <Logo className="h-full w-full" />
                </div>
              </div>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-parchment/15">
            {FACTS.map((f, i) => (
              <Reveal key={f.label} delay={(i % 3) * 0.08}>
                <div className="p-10 border-b border-parchment/15 lg:[&:nth-child(3n+1)]:border-r lg:[&:nth-child(3n+2)]:border-r lg:[&:nth-child(3n+2)]:border-parchment/15 lg:[&:nth-child(3n+1)]:border-parchment/15">
                  <div className="text-[9px] uppercase tracking-[0.32em] font-semibold text-magenta">
                    {f.label}
                  </div>
                  <div className="mt-6 font-display font-light text-3xl md:text-[36px] tracking-[-0.02em] text-parchment leading-tight">
                    {f.value}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-parchment">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-16 py-24 md:py-36 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
              Our philosophy
            </div>
            <h2 className="font-display font-light text-5xl md:text-7xl lg:text-8xl mt-10 tracking-[-0.04em] leading-[0.94] text-aubergine text-balance">
              Business is not{" "}
              <span className="italic">a transaction.</span> It is an{" "}
              <span className="italic text-magenta">enduring relationship</span>{" "}
              between two partners.
            </h2>
            <p className="mt-12 max-w-2xl mx-auto text-lg text-ink/70 leading-relaxed">
              Because we have been in this business for years, we understand
              your needs and expectations — and we strive to deliver the very
              best, every single time.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="mt-14 inline-flex items-center gap-3 bg-aubergine hover:bg-magenta text-parchment text-[11px] tracking-[0.28em] uppercase font-semibold px-8 py-5 transition-colors duration-300"
            >
              Become a Partner
              <span className="h-px w-6 bg-parchment" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
